import axios from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { getAuthority, loginRequest, msalConfig } from '../authConfig';
import configData from '../config.json';
import { appInsights } from '../services/ApplicationInsightsService';

const searchParams = new URLSearchParams(window.location.search);
const idTokenHint = searchParams.get('id_token_hint');
const authority = getAuthority(idTokenHint);
const msalInstance = new PublicClientApplication(msalConfig(authority));

export const API_URL = `${configData.REACT_APP_BACKEND_URL}/api`;
export const TNE_API_URL = `${configData.REACT_APP_TANDE_URL}/api`;

function refreshAccessToken() {
  return msalInstance.acquireTokenSilent({
    scopes: loginRequest.scopes,
    account: {}
  });
}

const axiosClient = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

axiosClient.interceptors.request.use(
  (config) => {
    const AirswiftAccessToken = localStorage.getItem('AirswiftAccessToken');
    if (AirswiftAccessToken) {
      config.headers['Authorization'] = `Bearer ${AirswiftAccessToken}`;
    }

    config.headers['Content-Type'] = 'application/json;charset=utf-8';
    config.headers['Access-Control-Allow-Origin'] = '*';
    config.headers['Access-Control-Allow-Credentials'] = 'true';
    config.headers['Access-Control-Allow-Methods'] = 'GET,PUT,PATCH,POST,DELETE,HEAD,OPTIONS';
    appInsights.trackEvent({
      name: `API Request from base url: ${config?.baseURL} and api ${config?.url}`,
      requestData: config?.data
    });
    return config;
  },
  (error) => {
    appInsights.trackException({
      name: 'API Request failed',
      isAccessDenied: error?.response?.status === 401,
      error: error.response
    });
    return Promise.reject(error);
  }
);

axiosClient.interceptors.response.use(
  (response) => {
    appInsights.trackEvent({
      name: `API Response from base url: ${response?.config?.baseURL} and api ${response?.config?.url}`,
      statusCode: response?.status
    });
    return response;
  },
  (error) => {
    const originalRequest = error.config;

    appInsights.trackException({
      name: 'API Request failed',
      error,
      statusCode: error.response.status
    });

    if (error.response && error.response.status === 401) {
      return refreshAccessToken()
        .then(({ accessToken }) => {
          localStorage.removeItem('AirswiftAccessToken');
          localStorage.setItem('AirswiftAccessToken', accessToken);
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          // eslint-disable-next-line no-underscore-dangle
          originalRequest._retry = true;

          appInsights.trackEvent({
            name: 'refresh token issued successfully',
            originalRequest
          });

          return axios(originalRequest);
        })
        .catch((err) => {
          appInsights.trackException({
            name: 'refresh token exception',
            isAccessDenied: error?.response?.status === 401,
            error: error.response
          });
          msalInstance
            .logoutRedirect({ ...loginRequest, state: JSON.stringify({ navigate: window.location.pathname }) })
            .catch((e) => {
              appInsights.trackException({
                name: 'azure b2c msal instance exception and logged out successfully',
                path: window.location.pathname,
                error: e
              });
              console.error(e);
            });
          return Promise.reject(err);
        });
    }
    if (error.response && error.response.status === 403) {
      error.response.data = JSON.parse('{"error": "Sorry. Not enough permissions."}');
      appInsights.trackException({
        name: 'Error Permission Denied',
        error: 'Sorry. Not enough permissions',
        statusCode: error.response.status
      });
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export const tneAxiosClient = axios.create({
  baseURL: TNE_API_URL,
  withCredentials: true
});

tneAxiosClient.interceptors.request.use(
  (config) => {
    const AirswiftAccessToken = localStorage.getItem('AirswiftAccessToken');
    if (AirswiftAccessToken) {
      config.headers['Authorization'] = `Bearer ${AirswiftAccessToken}`;
    }

    config.headers['Content-Type'] = 'application/json;charset=utf-8';
    config.headers['Access-Control-Allow-Origin'] = '*';
    config.headers['Access-Control-Allow-Credentials'] = 'true';
    config.headers['Access-Control-Allow-Methods'] = 'GET,PUT,PATCH,POST,DELETE,HEAD,OPTIONS';
    return config;
  },
  (error) => Promise.reject(error)
);

tneAxiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401) {
      return refreshAccessToken()
        .then(({ accessToken }) => {
          localStorage.setItem('AirswiftAccessToken', accessToken);
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          // eslint-disable-next-line no-underscore-dangle
          originalRequest._retry = true;

          return axios(originalRequest);
        })
        .catch((err) => {
          // TODO need to decide how to handle in the future
          // msalInstance
          //   .loginRedirect({ ...loginRequest, state: JSON.stringify({ navigate: window.location.pathname }) })
          //   .catch((e) => {
          //     console.error(e);
          //   });
          return Promise.reject(err);
        });
    }
    if (error.response && error.response.status === 403) {
      error.response.data = JSON.parse('{"error": "Sorry. Not enough permissions."}');
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
